import { useEffect } from 'react';

import { EntitlementSlugEnum } from '@electricjs/core_entity-client';

import { useGlobalUI } from '@/components/GlobalUIProvider';
import AlmostThereModal from '@/components/People/AlmostThereModal';
import { useGetOrganizationEmployeesCountQuery } from '@/redux/slices/organizationApiSlice';
import { useOrganizationHasEntitlement } from '@/hooks/useOrganizationHasEntitlement';
import { SessionStorageItems } from '@/types/sessionStorageItems';

type Props = {
  isReady: boolean;
  organizationId: string | undefined;
};

/**
 * Shows a one-time "Almost there!" modal for isolved-only orgs that have just
 * one employee, since that means employee data is still being synced in from
 * isolved and hasn't populated the Asset Hub yet.
 */
export const useShowAlmostThereModal = ({ isReady, organizationId }: Props) => {
  const { openModal, closeModal } = useGlobalUI();
  const hasIsolvedOnlyEntitlement = useOrganizationHasEntitlement(
    EntitlementSlugEnum.IsolvedOnly
  );
  const { data: employeeCount } = useGetOrganizationEmployeesCountQuery(
    { organizationId: organizationId ?? '' },
    { skip: !organizationId }
  );

  useEffect(() => {
    if (!isReady || !organizationId || employeeCount === undefined) return;
    if (!hasIsolvedOnlyEntitlement || employeeCount !== 1) return;

    const sessionKey = `${SessionStorageItems.ALMOST_THERE_MODAL_SHOWN}_${organizationId}`;
    if (sessionStorage.getItem(sessionKey) === 'true') return;

    sessionStorage.setItem(sessionKey, 'true');
    openModal(<AlmostThereModal onClose={closeModal} />);
  }, [
    isReady,
    organizationId,
    employeeCount,
    hasIsolvedOnlyEntitlement,
    openModal,
    closeModal,
  ]);
};
